import React, { useMemo, FC, useEffect } from 'react';
import { Table, useTable, Tooltip, Badge } from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';

import { PatentOfficeNames } from '../../../constants';
import {
  brandController,
  BrandDetail,
  BrandStatus,
  BrandTrademark,
} from '@/apis/core';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';
import { STATUS_BADGE_COLOR_MAP } from '@/constants/enum-mapping';

const TABLE_HEADERS = [
  {
    id: 'registrationId',
    label: 'Registration Number',
    sortable: false,
  },
  {
    id: 'trademarkText',
    label: 'Trademark Wording',
    sortable: false,
  },
  {
    id: 'owner',
    label: 'Trademark Owner',
    sortable: false,
  },
  {
    id: 'patentOffice',
    label: 'Trademark Office',
    sortable: false,
  },
  {
    id: 'validUntil',
    label: 'Expiration Date',
    sortable: false,
  },
  {
    id: 'outcome',
    label: 'Status',
    sortable: false,
  },
];

const TrademarkTab: FC<{
  brand: BrandDetail;
  onDataChange: (trademarks: BrandTrademark[]) => unknown;
}> = ({ brand, onDataChange }) => {
  const { uid: brandUid, status } = brand;
  const isDeleted = status === BrandStatus.Deleted;
  const table = useTable<BrandTrademark>(
    async () => {
      const records =
        !isDeleted && brandUid
          ? (await brandController.getTrademarks(brandUid)) || []
          : [];

      return {
        data: records,
        total: records.length,
        page: 1,
        rowsPerPage: Math.max(records.length, 10),
      };
    },
    {
      deps: [brandUid],
      inMemory: true,
    }
  );

  useEffect(() => {
    onDataChange?.(table.tableData);
  }, [table.tableData]);

  const tableDisplayData = useMemo(() => {
    return table.tableData.map((d) => ({
      registrationId: (
        <span data-testid="registrationId">{d.registrationId || 'N/A'}</span>
      ),
      trademarkText: (
        <span data-testid="trademarkText">{d.trademarkText || 'N/A'}</span>
      ),
      owner: <span data-testid="owner">{d.owner || 'N/A'}</span>,
      patentOffice: (
        <span data-testid="patentOffice">
          {d.patentOffice
            ? PatentOfficeNames[d.patentOffice] ?? d.patentOffice
            : 'N/A'}
        </span>
      ),
      validUntil: (
        <span data-testid="validUntil">
          {convertTimeWithTimezone(d.validUntil, undefined, 'MM/DD/YYYY') ||
            'N/A'}
        </span>
      ),
      outcome: d.outcome ? (
        <Badge
          size="md"
          shape="rectangle"
          variant="fill"
          type={STATUS_BADGE_COLOR_MAP[d.outcome] || 'neutral'}
          endAdornment={
            d.description ? (
              <Tooltip
                title={d.description}
                style={{ fontSize: '10px', color: 'inherit' }}
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            ) : undefined
          }
          data-testid="outcome"
        >
          {humanize(d.outcome)}
        </Badge>
      ) : (
        <span data-testid="outcome">N/A</span>
      ),
    }));
  }, [table.tableData]);

  return (
    <Table
      emptyBlockHeight="180px"
      loading={false}
      headRows={TABLE_HEADERS}
      emptyState="no trademarks to view"
      disableHover={true}
      data={tableDisplayData}
      data-testid="trademarkTable"
    />
  );
};

export default TrademarkTab;
